// Thin JSON client for the Worker API. Every failure becomes an ApiError with a
// message that can be shown to the user as-is.
import type { EarlyUserStatus } from "../../shared/ai-credits";

export interface Issue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }

  get issues(): Issue[] {
    return Array.isArray(this.body.issues) ? (this.body.issues as Issue[]) : [];
  }

  /** True when the request never reached the server (offline, DNS, CORS, aborted). */
  get offline(): boolean {
    return this.status === 0;
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set("content-type", "application/json");
  let res: Response;
  try {
    res = await fetch(path, { ...rest, headers, body: json !== undefined ? JSON.stringify(json) : rest.body, credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "You're offline or the server can't be reached. Your changes are kept on this device.", "offline");
  }
  const text = await res.text();
  let body: Record<string, unknown> = {};
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = {};
    }
  }
  if (!res.ok) {
    const message =
      typeof body.error === "string"
        ? body.error
        : res.status >= 500
          ? "The server had a problem. Please try again in a minute."
          : `Request failed (${res.status}).`;
    throw new ApiError(res.status, message, typeof body.code === "string" ? body.code : "error", body);
  }
  return body as T;
}

export interface Me {
  user: { id: string; email: string; name: string | null };
  role: "admin" | "editor" | "viewer";
  /** suspended is missing from servers older than workspace suspension. */
  org: { id: string; name: string; timezone: string; storageBytes: number; storageQuotaBytes: number; suspended?: boolean };
  members: { id: string; email: string; name: string | null; role: string }[];
  limits: { maxFileBytes: number };
  push: { publicKey: string | null };
  accounts: SignInAccounts;
  /** Missing from servers older than this field. */
  preferences?: { aiFullScan: boolean };
  /** Listed in OPERATOR_EMAILS: sees the admin dashboard. */
  operator?: boolean;
  /** Adding contracts by email. Missing from servers older than the Inbox. */
  inbox?: { enabled: boolean; waiting: number };
}

export interface SignInAccounts {
  emails: { email: string; primary: boolean }[];
  google: { email: string }[];
}

export interface PublicConfig {
  googleEnabled: boolean;
  turnstileSiteKey: string | null;
  environment: string;
  /** Missing from servers older than the early-user campaign. */
  earlyUsers?: EarlyUserStatus;
}
